import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Auth } from "aws-amplify";

// Custom Components
import InstructionsComponent from "./InstructionsComponent";
import FileUploaderComponent from "./FileUploaderComponent";
import TexPdfViewer from "./TexPdfViewer";
import MarkdownArtifactViewer from "./MarkdownArtifactViewer";
import Button from "components/CustomButtons/Button.js";
import HeadingWithDescription from "components/Heading/HeadingWithDescription";
import NotificationContext from "views/Components/Context.js";

// API functions
import {
  getProject,
  runPipeline,
  listInputs,
  uploadInstructions,
  listArtifacts,
  updateProject,
  listConceptLabsForArtifacts,
  getConceptLabFilesForArtifacts,
} from "views/APIs/NewProjectArtifacts";

// Constants
import {
  DISPLAY_STAGES,
  PIPELINE_KEYS,
  STAGE_DESCRIPTIONS,
  StageHeader,
  DEFAULT_INSTRUCTIONS,
  mapStatusToStep,
  isQueueStatus,
  getQueueLoadingMessage,
  getExpectedTimeMessage,
} from "./artifactProjectConstants";

// Styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { stepperStyles, QontoConnector } from "views/Styles/stepperStyles";
import appbarStyles from "views/Styles/appbarStyles";

const useStyles = makeStyles((theme) => ({
  ...styles,
  ...stepperStyles(theme),
  ...appbarStyles(theme),
  stageContainer: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "#fff",
    borderRadius: "8px",
    boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
  },
  nextButtonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid #e0e0e0",
  },
  pathChoiceContainer: {
    marginTop: theme.spacing(3),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid #e0e0e0",
  },
  pathChoiceCards: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  pathChoiceCard: {
    flex: 1,
    padding: theme.spacing(3),
    borderRadius: "8px",
    border: "2px solid #e0e0e0",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#20677b",
      backgroundColor: "#f0f7fa",
    },
  },
  pathChoiceCardPrimary: {
    flex: 1,
    padding: theme.spacing(3),
    borderRadius: "8px",
    border: "2px solid #20677b",
    cursor: "pointer",
    backgroundColor: "#f0f7fa",
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "#e0eff5",
    },
  },
  selectedPathHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 2),
    backgroundColor: "#f0f7fa",
    borderRadius: "4px",
    borderLeft: "4px solid #20677b",
  },
  loadingOverlay: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "50vh",
    padding: theme.spacing(4),
  },
  sectionDivider: {
    margin: theme.spacing(3, 0),
  },
  nextStepSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "#f0f7fa",
    borderRadius: "8px",
    border: "1px solid #b0d4df",
  },
  nextStepSectionTitle: {
    fontWeight: 600,
    color: "#20677b",
    marginBottom: theme.spacing(2),
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  inlineSection: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: "1px solid #dbeaf0",
  },
  compactCardGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1),
  },
  compactCard: {
    padding: theme.spacing(1.5),
    borderRadius: "8px",
    border: "1px solid #c9dfe7",
    cursor: "pointer",
    backgroundColor: "#fff",
  },
  compactCardActive: {
    border: "2px solid #20677b",
    backgroundColor: "#eaf4f8",
  },
  filePreviewContainer: {
    marginTop: theme.spacing(1.5),
    border: "1px solid #cfe2e9",
    borderRadius: "8px",
    backgroundColor: "#fff",
    maxHeight: 260,
    overflow: "auto",
    padding: theme.spacing(1.5),
  },
  filePreviewCode: {
    margin: 0,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: "0.78rem",
    lineHeight: 1.45,
    fontFamily: "Monaco, Menlo, Consolas, monospace",
  },
}));

// Loading step icon for stepper
const LoadingStepIcon = () => <CircularProgress size={16} />;

export default function ArtifactProjectBoard(props) {
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);

  // Extract projectId from URL params
  const { projectId } = props.match.params;

  // State
  const [loading, setLoading] = useState({ isLoading: true, message: "Loading Project..." });
  const [project, setProject] = useState({});
  const [projectStatus, setProjectStatus] = useState("");
  const [user, setUser] = useState("");
  const [artifacts, setArtifacts] = useState([]);

  // Track selected artifact IDs for each stage (for pipeline dependencies)
  const [selectedCaseStudyPdfId, setSelectedCaseStudyPdfId] = useState(null);
  const [selectedOutlineId, setSelectedOutlineId] = useState(null);
  const [selectedSlideDeckPdfId, setSelectedSlideDeckPdfId] = useState(null);
  const [selectedLectureNotesPdfId, setSelectedLectureNotesPdfId] = useState(null);

  // Stepper state
  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab] = useState(0);

  // Pipeline execution state
  const [runningPipeline, setRunningPipeline] = useState(false);
  const [inBetweenMessage, setInBetweenMessage] = useState("");

  // Content suppression state (for navigating back during loading)
  const [suppressContentOnly, setSuppressContentOnly] = useState(false);

  // Instructions and file selections for pipeline
  const [selectedInstructionsId, setSelectedInstructionsId] = useState(null);
  const [selectedInstructionsStage, setSelectedInstructionsStage] = useState(null);
  const [selectedFileIdsByStage, setSelectedFileIdsByStage] = useState({
    0: [],
    1: [],
    2: [],
    3: [],
    4: [],
    5: [],
  });
  const [selectedTemplate, setSelectedTemplate] = useState("GMU");

  // Resources stage: user choice for what to generate next
  // - case_study: run pipeline 1
  // - outline: skip case study and run pipeline 3a
  const [resourcesNextChoice, setResourcesNextChoice] = useState(null);
  const [caseStudyAttachmentSource, setCaseStudyAttachmentSource] = useState("project_files");
  const [conceptLabs, setConceptLabs] = useState([]);
  const [loadingConceptLabs, setLoadingConceptLabs] = useState(false);
  const [loadedConceptLabs, setLoadedConceptLabs] = useState(false);
  const [selectedConceptLabId, setSelectedConceptLabId] = useState("");
  const [conceptLabFiles, setConceptLabFiles] = useState([]);
  const [loadingConceptLabFiles, setLoadingConceptLabFiles] = useState(false);

  const resourcesChoiceStorageKey = useMemo(() => {
    if (!projectId) return null;
    return `artifactProject:resourcesNextChoice:${projectId}`;
  }, [projectId]);

  const selectedConceptLab = useMemo(
    () => conceptLabs.find((lab) => lab.lab_id === selectedConceptLabId) || null,
    [conceptLabs, selectedConceptLabId]
  );

  const availableConceptLabFiles = useMemo(
    () => conceptLabFiles.filter((f) => f.exists),
    [conceptLabFiles]
  );

  useEffect(() => {
    setConceptLabs([]);
    setLoadedConceptLabs(false);
    setSelectedConceptLabId("");
    setConceptLabFiles([]);
  }, [projectId]);

  // Track which instruction stages we've already ensured exist for this project.
  const ensuredInstructionStagesRef = useRef(new Set());
  const instructionsComponentRef = useRef(null);

  const ensureInstructionsForStages = async (stages) => {
    if (!projectId) return;
    const uniqueStages = Array.from(new Set(stages)).filter(
      (s) => s !== null && s !== undefined
    );
    const stagesToCheck = uniqueStages.filter(
      (s) => !ensuredInstructionStagesRef.current.has(`${projectId}:${s}`)
    );
    if (stagesToCheck.length === 0) return;

    // Mark in-flight to prevent stampedes.
    stagesToCheck.forEach((s) =>
      ensuredInstructionStagesRef.current.add(`${projectId}:${s}`)
    );

    try {
      const inputsData = await listInputs(projectId);
      const instructions = inputsData.instructions || [];

      for (const stageIndex of stagesToCheck) {
        const has = instructions.some((i) => i.pipeline_stage === stageIndex);
        if (!has) {
          const defaultContent =
            DEFAULT_INSTRUCTIONS[stageIndex] ||
            `# Instructions for Stage ${stageIndex}\n\nAdd your instructions here...`;
          await uploadInstructions(projectId, defaultContent, stageIndex);
        }
      }
    } catch (e) {
      // Allow retry later if something transient failed.
      stagesToCheck.forEach((s) =>
        ensuredInstructionStagesRef.current.delete(`${projectId}:${s}`)
      );
      console.warn("Failed to ensure instructions:", e);
    }
  };

  // Computed values
  const isInBetween = useMemo(() => isQueueStatus(projectStatus), [projectStatus]);
  const stepperInBetween = isInBetween;
  const contentInBetween = useMemo(
    () => isInBetween && !suppressContentOnly,
    [isInBetween, suppressContentOnly]
  );

  const baseStepIndex = useMemo(
    () => mapStatusToStep(projectStatus?.trim?.() ?? projectStatus),
    [projectStatus]
  );

  // Auto-create instructions as stages unlock.
  // When the user reaches a stage, we ensure the instructions needed for the *next* pipeline exist.
  useEffect(() => {
    const stagesToEnsure = [];
    if (baseStepIndex >= 1) stagesToEnsure.push(1); // outline creation
    if (baseStepIndex >= 2) stagesToEnsure.push(2); // slide deck creation
    if (baseStepIndex >= 3) stagesToEnsure.push(3); // lecture notes creation
    if (baseStepIndex >= 4) stagesToEnsure.push(4); // slide deck with notes creation

    if (stagesToEnsure.length === 0) return;
    ensureInstructionsForStages(stagesToEnsure);
  }, [projectId, baseStepIndex]);

  const WAIT_MSG = useMemo(() => {
    if (!isQueueStatus(projectStatus)) return "";
    return getExpectedTimeMessage();
  }, [projectStatus]);

  // Stepper labels with optional loading step
  const stepsWithIntermediate = useMemo(() => {
    if (!stepperInBetween) return DISPLAY_STAGES;
    const insertAt = baseStepIndex + 1;
    return [
      ...DISPLAY_STAGES.slice(0, insertAt),
      "Loading…",
      ...DISPLAY_STAGES.slice(insertAt),
    ];
  }, [stepperInBetween, baseStepIndex]);

  const computedActiveStep = useMemo(
    () => (stepperInBetween ? baseStepIndex + 1 : baseStepIndex),
    [stepperInBetween, baseStepIndex]
  );

  const completedTill = useMemo(() => baseStepIndex, [baseStepIndex]);

  // Determine which outline artifact type to display based on what was generated
  const outlineArtifactType = useMemo(() => {
    return artifacts.some((a) => a.type === "case_study_outline_md")
      ? "case_study_outline_md"
      : "lecture_outline_md";
  }, [artifacts]);

  // Reset content suppression when status changes
  useEffect(() => {
    setSuppressContentOnly(false);
  }, [projectStatus]);

  // Update in-between message
  useEffect(() => {
    const base = getQueueLoadingMessage(projectStatus);
    setInBetweenMessage(base);
  }, [projectStatus]);

  // Fetch user
  useEffect(() => {
    async function fetchUser() {
      try {
        const u = await Auth.currentAuthenticatedUser();
        setUser(u);
      } catch (error) {
        console.error("Error fetching user:", error);
        showNotification("Error fetching user details", "danger");
      }
    }
    fetchUser();
  }, []);

  // WebSocket for long-running tasks
  useEffect(() => {
    if (!user || !projectId || !isQueueStatus(projectStatus)) {
      return;
    }

    const wsUrl = `${process.env.REACT_APP_FASTAPI_URL.replace(
      "http",
      "ws"
    )}/ws/tasks/${user.username}/${projectId}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => console.log("Task WebSocket connected");
    ws.onmessage = (event) => {
      if (event.data === "Done") {
        setInBetweenMessage("Done!");
        window.location.reload();
      } else {
        setInBetweenMessage("Failed!");
        setProjectStatus("Failed");
        window.location.reload();
      }
    };
    ws.onclose = () => console.log("Task WebSocket disconnected");

    return () => ws.close();
  }, [user, projectId, projectStatus]);

  // Fetch project details
  useEffect(() => {
    const fetchProjectDetails = async () => {
      if (!projectId) return;

      setLoading({ isLoading: true, message: "Loading Project..." });
      try {
        const data = await getProject(projectId);
        const projectData = data.project || {};
        setProject(projectData);
        setProjectStatus(projectData.status_text || getStatusText(projectData.status));
        setActiveTab(mapStatusToStep(projectData.status_text || getStatusText(projectData.status)));

        // Fetch artifacts
        const artifactsData = await listArtifacts(projectId);
        setArtifacts(artifactsData || []);
      } catch (error) {
        console.error("Error fetching project:", error);
        showNotification("Error loading project", "danger");
      } finally {
        setLoading({ isLoading: false, message: "" });
      }
    };

    fetchProjectDetails();
  }, [projectId]);

  // Restore the user's last Resources-stage choice (per project).
  useEffect(() => {
    if (!resourcesChoiceStorageKey) return;
    try {
      const saved = window.localStorage.getItem(resourcesChoiceStorageKey);
      if (saved === "case_study" || saved === "outline") {
        setResourcesNextChoice(saved);
      }
    } catch (e) {
      // Non-fatal (private mode, storage disabled, etc.)
      console.warn("Unable to read resources choice from localStorage", e);
    }
  }, [resourcesChoiceStorageKey]);

  // Persist Resources-stage choice when user picks it.
  useEffect(() => {
    if (!resourcesChoiceStorageKey) return;
    if (resourcesNextChoice !== "case_study" && resourcesNextChoice !== "outline") return;
    try {
      window.localStorage.setItem(resourcesChoiceStorageKey, resourcesNextChoice);
    } catch (e) {
      console.warn("Unable to save resources choice to localStorage", e);
    }
  }, [resourcesChoiceStorageKey, resourcesNextChoice]);

  // If no choice was restored, infer it from current project state/artifacts.
  // This covers scenarios where the user already generated something.
  useEffect(() => {
    if (resourcesNextChoice) return;

    const hasCaseStudyPdf = artifacts.some((a) => a.type === "case_study_pdf");
    const hasAnyOutline = artifacts.some(
      (a) => a.type === "lecture_outline_md" || a.type === "case_study_outline_md"
    );
    const statusText = (projectStatus || "").toLowerCase();

    if (hasCaseStudyPdf || statusText.includes("case study")) {
      setResourcesNextChoice("case_study");
      return;
    }

    if (hasAnyOutline || statusText.includes("outline")) {
      setResourcesNextChoice("outline");
    }
  }, [resourcesNextChoice, artifacts, projectStatus]);

  useEffect(() => {
    const shouldLoadConceptLabs =
      projectId &&
      resourcesNextChoice === "case_study" &&
      caseStudyAttachmentSource === "concept_lab";

    if (!shouldLoadConceptLabs) return;
    if (loadedConceptLabs || loadingConceptLabs) return;

    const loadConceptLabs = async () => {
      setLoadingConceptLabs(true);
      try {
        const labs = await listConceptLabsForArtifacts(projectId);
        setConceptLabs(Array.isArray(labs) ? labs : []);
      } catch (error) {
        console.error("Failed to load concept labs:", error);
        showNotification("Unable to load concept labs", "warning");
      } finally {
        setLoadedConceptLabs(true);
        setLoadingConceptLabs(false);
      }
    };

    loadConceptLabs();
  }, [
    projectId,
    resourcesNextChoice,
    caseStudyAttachmentSource,
    loadedConceptLabs,
    loadingConceptLabs,
    showNotification,
  ]);

  useEffect(() => {
    const shouldFetchPreview =
      projectId &&
      resourcesNextChoice === "case_study" &&
      caseStudyAttachmentSource === "concept_lab" &&
      selectedConceptLabId;

    if (!shouldFetchPreview) {
      setConceptLabFiles([]);
      return;
    }

    const fetchConceptLabFiles = async () => {
      setLoadingConceptLabFiles(true);
      try {
        const data = await getConceptLabFilesForArtifacts(projectId, selectedConceptLabId);
        const files = Array.isArray(data?.files) ? data.files : [];
        setConceptLabFiles(files);
      } catch (error) {
        console.error("Failed to fetch concept lab file previews:", error);
        setConceptLabFiles([]);
        showNotification("Unable to fetch selected concept lab files", "warning");
      } finally {
        setLoadingConceptLabFiles(false);
      }
    };

    fetchConceptLabFiles();
  }, [
    projectId,
    resourcesNextChoice,
    caseStudyAttachmentSource,
    selectedConceptLabId,
    showNotification,
  ]);

  // Update stepper positions
  useEffect(() => {
    setActiveStep(computedActiveStep);
  }, [computedActiveStep, completedTill]);

  // Helper to convert numeric status to text
  // NOTE: Numeric statuses correspond to old pipeline stage values (pre-case-study).
  // status_text (set by backend) takes priority and handles "Case Study Generated".
  const getStatusText = (status) => {
    const statusMap = {
      0: "Not Started",
      1: "Outline Generated",
      2: "Slide Deck Generated",
      3: "Lecture Notes Generated",
      4: "Slide Deck with Notes Generated",
    };
    return statusMap[status] ?? "Not Started";
  };

  // Handle step click
  const handleStepChange = (index) => {
    const canAccess = baseStepIndex >= index;
    if (!canAccess) {
      showNotification("Complete previous steps first", "warning");
      return;
    }

    if (isInBetween && index <= baseStepIndex) {
      setSuppressContentOnly(true);
      setActiveTab(index);
      setActiveStep(computedActiveStep);
      return;
    }

    setActiveTab(index);
    if (!isInBetween) {
      setActiveStep(index);
    }
  };

  // Handle project details update
  const handleProjectDetailsChange = async (newDetails) => {
    try {
      await updateProject(projectId, {
        name: newDetails.title,
        description: newDetails.shortDescription,
      });
      setProject((prev) => ({
        ...prev,
        name: newDetails.title,
        description: newDetails.shortDescription,
      }));
      showNotification("Project details updated successfully", "success");
    } catch (error) {
      showNotification("Error updating project details", "danger");
    }
  };

  // Handle pipeline execution
  const handleRunPipeline = async (pipelineKey) => {
    try {
      // Get inputs for the pipeline
      const inputsData = await listInputs(projectId);
      const instructions = inputsData.instructions || [];

      const expectedInstructionStageByPipeline = {
        "1": 0,
        "3a": 1,
        "3c": 1,
        "4": 2,
        "5": 3,
        "6": 4,
      };
      const expectedStage = expectedInstructionStageByPipeline[pipelineKey];

      // If missing, auto-create expected stage instructions and re-fetch once.
      if (expectedStage !== undefined) {
        const hasExpected = instructions.some((i) => i.pipeline_stage === expectedStage);
        if (!hasExpected) {
          await ensureInstructionsForStages([expectedStage]);
        }
      }

      const inputsDataAfterEnsure = await listInputs(projectId);
      const instructionsAfterEnsure = inputsDataAfterEnsure.instructions || [];

      if (instructionsAfterEnsure.length === 0) {
        showNotification("Please add instructions first", "warning");
        return;
      }

      const stageMatchedSelected =
        expectedStage === undefined || selectedInstructionsStage === expectedStage;
      const selectedOk = stageMatchedSelected && selectedInstructionsId;
      const stageMatch =
        expectedStage === undefined
          ? null
          : instructionsAfterEnsure.find((i) => i.pipeline_stage === expectedStage);
      const fallback = instructionsAfterEnsure[0];
      const resolvedInstructionsId =
        selectedOk || stageMatch?.input_file_id || fallback?.input_file_id;

      if (!resolvedInstructionsId) {
        showNotification("Please add instructions first", "warning");
        return;
      }

      // Build pipeline payload
      const fileSelectionStageByPipeline = {
        "1": 0, // resources -> case study
        "3a": 0, // resources -> outline (skip case study)
        "3c": 1, // case study -> outline
        "4": 2, // outline -> slide deck
        "5": 3, // slide deck -> lecture notes
        "6": 4, // lecture notes -> slide deck with notes
      };
      const fileStageForPipeline = fileSelectionStageByPipeline[pipelineKey];
      const fileIdsForPipeline =
        fileStageForPipeline === undefined
          ? []
          : selectedFileIdsByStage[fileStageForPipeline] || [];

      const payload = {
        instructions_file_id: resolvedInstructionsId,
        file_ids: fileIdsForPipeline,
        template: selectedTemplate,
      };

      console.log(`Pipeline ${pipelineKey} payload before adding artifacts:`, payload);

      // Add pipeline-specific artifact requirements based on schema
      switch (pipelineKey) {
        case "1": // Pipeline 1: Case Study — no extra artifact requirements
          if (caseStudyAttachmentSource === "concept_lab" && selectedConceptLabId) {
            payload.concept_lab_id = selectedConceptLabId;
          }
          break;

        case "3c": { // Pipeline 3c: Case Study Outline — requires case_study_pdf_artifact_id
          let caseStudyId = selectedCaseStudyPdfId;

          if (!caseStudyId) {
            const allArtifacts = await listArtifacts(projectId);
            const caseStudies = allArtifacts.filter((a) => a.type === "case_study_pdf");
            caseStudyId = caseStudies.at(-1)?.artifact_id || null;
          }

          if (!caseStudyId) {
            showNotification(
              "Case Study PDF required. Please generate the case study first.",
              "warning"
            );
            return;
          }
          payload.case_study_pdf_artifact_id = caseStudyId;
          break;
        }

        case "4": // Pipeline 4: Slide Deck (requires outline_md from stage 2)
          if (!selectedOutlineId) {
            showNotification("Outline.md artifact required. Please view the outline in Stage 2 first.", "warning");
            return;
          }
          payload.outline_md_artifact_id = selectedOutlineId;
          payload.run_llm_as_a_judge = false;
          break;

        case "5": // Pipeline 5: Lecture Notes (requires slide_deck_pdf from stage 3)
          if (!selectedSlideDeckPdfId) {
            showNotification("Slide Deck.pdf artifact required. Please view the slide deck in Stage 3 first.", "warning");
            return;
          }
          payload.slide_deck_pdf_artifact_id = selectedSlideDeckPdfId;
          break;

        case "6": { // Pipeline 6: Deck with Notes (requires both slide_deck_pdf + lecture_notes_pdf)
          let slideDeckId = selectedSlideDeckPdfId;
          let lectureNotesId = selectedLectureNotesPdfId;

          if (!slideDeckId || !lectureNotesId) {
            const allArtifacts = await listArtifacts(projectId);
            if (!slideDeckId) {
              const slideDecks = allArtifacts.filter((a) => a.type === "slide_deck_pdf");
              slideDeckId = slideDecks.at(-1)?.artifact_id || null;
            }
            if (!lectureNotesId) {
              const lectureNotes = allArtifacts.filter((a) => a.type === "lecture_notes_pdf");
              lectureNotesId = lectureNotes.at(-1)?.artifact_id || null;
            }
          }

          const missing6 = [];
          if (!slideDeckId) missing6.push("Slide Deck PDF (Stage 3)");
          if (!lectureNotesId) missing6.push("Lecture Notes PDF (Stage 4)");
          if (missing6.length > 0) {
            showNotification(`Missing required artifacts: ${missing6.join(", ")}. Please ensure they have been generated.`, "warning");
            return;
          }
          payload.slide_deck_pdf_artifact_id = slideDeckId;
          payload.lecture_notes_pdf_artifact_id = lectureNotesId;
          break;
        }

        default:
          break;
      }

      console.log(`Running pipeline ${pipelineKey} with payload:`, payload);

      // Set queue status immediately to trigger page loader
      const queueStatusMap = {
        "1": "In Case Study Generation Queue",
        "3a": "In Outline Generation Queue",
        "3c": "In Outline Generation Queue",
        "4": "In Slide Deck Generation Queue",
        "5": "In Lecture Notes Generation Queue",
        "6": "In Slide Deck with Notes Generation Queue",
      };
      setProjectStatus(queueStatusMap[pipelineKey] || projectStatus);

      // Run the pipeline
      await runPipeline(projectId, pipelineKey, payload);
      showNotification("Pipeline started successfully", "success");

      // Refresh the page to get updated status
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error running pipeline:", error);
      showNotification(`Error running pipeline: ${error.message}`, "danger");
      setProjectStatus(projectStatus); // Reset status on error
    }
  };

  const handleGenerateWithAutoSave = async (pipelineKey) => {
    try {
      const saveApi = instructionsComponentRef.current;
      if (saveApi?.hasUnsavedChanges?.()) {
        showNotification(
          "Unsaved instruction changes detected. Saving before generation...",
          "info"
        );
      }

      if (saveApi?.saveIfNeeded) {
        const saved = await saveApi.saveIfNeeded();
        if (!saved) {
          showNotification(
            "Could not save instructions. Please resolve and try again.",
            "warning"
          );
          return;
        }
      }

      await handleRunPipeline(pipelineKey);
    } catch (error) {
      console.error("Error during auto-save before pipeline run:", error);
      showNotification("Failed to save instructions before generation", "danger");
    }
  };

  // Handle instructions change (track which stage those instructions correspond to)
  const handleInstructionsChangeForStage = (stageIndex) => (instructionId, content) => {
    setSelectedInstructionsId(instructionId);
    setSelectedInstructionsStage(stageIndex);
  };

  // Handle template change
  const handleTemplateChange = (template) => {
    setSelectedTemplate(template);
  };

  // Handle file selection change (stage-scoped)
  const handleFileSelectionChangeForStage = (stageIndex) => (fileIds) => {
    setSelectedFileIdsByStage((prev) => ({
      ...prev,
      [stageIndex]: Array.isArray(fileIds) ? fileIds : [],
    }));
  };

  // Intermediate content (loading state)
  const IntermediateContent = () => (
    <Box className={classes.loadingOverlay}>
      <CircularProgress className={classes.loaderClass} />
      <Typography variant="h5" style={{ marginTop: 16, textTransform: "none" }}>
        {WAIT_MSG || "Working on it…"}
      </Typography>
      {inBetweenMessage && (
        <Typography
          variant="body2"
          style={{ marginTop: 8, opacity: 0.8, textTransform: "none" }}
        >
          {inBetweenMessage}
        </Typography>
      )}
    </Box>
  );

  // Render stage content
  const renderStageContent = () => {
    if (contentInBetween) return <IntermediateContent />;

    switch (activeTab) {
      case 0: {
        // Resources Stage
        const resourcesInstructionsStage =
          resourcesNextChoice === "case_study" ? 0 : 1;
        const caseStudyConceptLabReady =
          caseStudyAttachmentSource !== "concept_lab" ||
          (!!selectedConceptLabId && !loadingConceptLabFiles);
        const resourcesReadyToRun =
          !!resourcesNextChoice &&
          !!selectedInstructionsId &&
          selectedInstructionsStage === resourcesInstructionsStage &&
          caseStudyConceptLabReady;

        return (
          <Box className={classes.stageContainer}>
            <Box className={classes.nextStepSection}>
              <Typography className={classes.nextStepSectionTitle}>
                Choose what to generate next
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
                Choose your path. Instructions will load based on your selection.
              </Typography>

              <Box className={classes.pathChoiceCards}>
                <Box
                  className={
                    resourcesNextChoice === "case_study"
                      ? classes.pathChoiceCardPrimary
                      : classes.pathChoiceCard
                  }
                  onClick={() => {
                    setResourcesNextChoice("case_study");
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    style={{
                      fontWeight: 700,
                      color: resourcesNextChoice === "case_study" ? "#20677b" : "#333",
                      marginBottom: 4,
                    }}
                  >
                    Generate Case Study
                  </Typography>
                  <Typography variant="body2" style={{ color: "#555" }}>
                    Create a case study from your source materials. It will be used as additional
                    context when generating the course outline.
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{ color: "#20677b", fontWeight: 600, marginTop: 8, display: "block" }}
                  >
                    Recommended — richer, more grounded content
                  </Typography>
                </Box>

                <Box
                  className={
                    resourcesNextChoice === "outline"
                      ? classes.pathChoiceCardPrimary
                      : classes.pathChoiceCard
                  }
                  onClick={() => {
                    setResourcesNextChoice("outline");
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    style={{
                      fontWeight: 700,
                      color: resourcesNextChoice === "outline" ? "#20677b" : "#333",
                      marginBottom: 4,
                    }}
                  >
                    Skip to Outline
                  </Typography>
                  <Typography variant="body2" style={{ color: "#555" }}>
                    Skip case study generation and go directly to outline creation from your
                    uploaded resources.
                  </Typography>
                  <Typography
                    variant="caption"
                    style={{ color: "#888", marginTop: 8, display: "block" }}
                  >
                    Faster path — fewer steps
                  </Typography>
                </Box>
              </Box>
            </Box>

            {resourcesNextChoice && (
              <>
                <InstructionsComponent
                  ref={instructionsComponentRef}
                  projectId={projectId}
                  stage={resourcesInstructionsStage}
                  onInstructionsChange={handleInstructionsChangeForStage(resourcesInstructionsStage)}
                  onTemplateChange={handleTemplateChange}
                  autoCreateIfMissing
                />
                <Divider className={classes.sectionDivider} />
              </>
            )}

            {resourcesNextChoice === "case_study" && (
              <Box className={classes.nextStepSection} style={{ marginTop: 0 }}>
                <Typography className={classes.nextStepSectionTitle}>
                  Additional Case Study Context
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Choose where extra context files come from.
                </Typography>

                <Box className={classes.compactCardGrid}>
                  <Box
                    className={`${classes.compactCard} ${
                      caseStudyAttachmentSource === "project_files" ? classes.compactCardActive : ""
                    }`}
                    onClick={() => {
                      setCaseStudyAttachmentSource("project_files");
                      setSelectedConceptLabId("");
                      setConceptLabFiles([]);
                    }}
                  >
                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>
                      Use Project Uploads
                    </Typography>
                    <Typography variant="body2" style={{ color: "#4a4a4a" }}>
                      Use only files uploaded in this artifact project.
                    </Typography>
                  </Box>

                  <Box
                    className={`${classes.compactCard} ${
                      caseStudyAttachmentSource === "concept_lab" ? classes.compactCardActive : ""
                    }`}
                    onClick={() => {
                      setCaseStudyAttachmentSource("concept_lab");
                    }}
                  >
                    <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>
                      Use Existing Concept Lab
                    </Typography>
                    <Typography variant="body2" style={{ color: "#4a4a4a" }}>
                      Attach `streamlit_specifications.md`, `app.py`, and `source.py` from a selected lab.
                    </Typography>
                  </Box>
                </Box>

                {caseStudyAttachmentSource === "concept_lab" && (
                  <Box className={classes.inlineSection}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel id="concept-lab-select-label">Select Concept Lab</InputLabel>
                      <Select
                        labelId="concept-lab-select-label"
                        value={selectedConceptLabId}
                        label="Select Concept Lab"
                        onChange={(e) => setSelectedConceptLabId(e.target.value)}
                      >
                        {conceptLabs.map((lab) => (
                          <MenuItem key={lab.lab_id} value={lab.lab_id}>
                            {lab.lab_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {loadingConceptLabs && (
                      <Typography variant="body2" style={{ marginTop: 8, color: "#666" }}>
                        Loading concept labs...
                      </Typography>
                    )}

                    {!loadingConceptLabs && conceptLabs.length === 0 && (
                      <Typography variant="body2" style={{ marginTop: 8, color: "#666" }}>
                        No concept labs found for this user.
                      </Typography>
                    )}

                    {selectedConceptLab && (
                      <Typography variant="body2" style={{ marginTop: 12, color: "#335" }}>
                        Selected: <strong>{selectedConceptLab.lab_name}</strong>
                      </Typography>
                    )}

                    {loadingConceptLabFiles && (
                      <Typography variant="body2" style={{ marginTop: 8, color: "#666" }}>
                        Fetching lab files preview...
                      </Typography>
                    )}

                    {!loadingConceptLabFiles && selectedConceptLabId && (
                      <>
                        <Typography variant="body2" style={{ marginTop: 10, color: "#444" }}>
                          Existing files found: {availableConceptLabFiles.length} / 3
                        </Typography>
                        {conceptLabFiles.map((file) => (
                          <Box key={file.file_path} className={classes.filePreviewContainer}>
                            <Typography variant="subtitle2" style={{ marginBottom: 6 }}>
                              {file.display_name} {file.exists ? "(found)" : "(not found)"}
                            </Typography>
                            {file.exists ? (
                              <pre className={classes.filePreviewCode}>{file.content || ""}</pre>
                            ) : (
                              <Typography variant="body2" style={{ color: "#777" }}>
                                This file does not exist in the selected lab repository.
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}

            <FileUploaderComponent
              projectId={projectId}
              stage={0}
              selectedFileIds={selectedFileIdsByStage[0]}
              onSelectionChange={handleFileSelectionChangeForStage(0)}
            />

            <Box className={classes.nextButtonContainer}>
              <Button
                color="quCreateColor"
                disabled={!resourcesReadyToRun}
                onClick={() =>
                  handleGenerateWithAutoSave(
                    resourcesNextChoice === "case_study"
                      ? PIPELINE_KEYS.resourcesCaseStudy
                      : PIPELINE_KEYS.resourcesNext
                  )
                }
              >
                {resourcesNextChoice === "outline" ? "Generate Outline →" : "Generate Case Study →"}
              </Button>
            </Box>
          </Box>
        );
      }

      case 1:
        // Case Study Stage
        return (
          <Box className={classes.stageContainer}>
            <TexPdfViewer
              projectId={projectId}
              stage={1}
              artifactType="case_study"
              onArtifactSelect={(artifactId) => setSelectedCaseStudyPdfId(artifactId)}
            />

            <Box className={classes.nextStepSection}>
              <Typography className={classes.nextStepSectionTitle}>
                Configure Next Step — Outline Generation
              </Typography>

              <InstructionsComponent
                ref={instructionsComponentRef}
                projectId={projectId}
                stage={1}
                onInstructionsChange={handleInstructionsChangeForStage(1)}
                onTemplateChange={handleTemplateChange}
                autoCreateIfMissing
              />

              <Divider className={classes.sectionDivider} />

              <FileUploaderComponent
                projectId={projectId}
                stage={1}
                selectedFileIds={selectedFileIdsByStage[1]}
                onSelectionChange={handleFileSelectionChangeForStage(1)}
              />

              <Box className={classes.nextButtonContainer}>
                <Button
                  color="quCreateColor"
                  onClick={() => handleGenerateWithAutoSave(PIPELINE_KEYS.caseStudyNext)}
                >
                  Generate Outline →
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 2:
        // Outline Stage
        return (
          <Box className={classes.stageContainer}>
            <MarkdownArtifactViewer
              projectId={projectId}
              stage={2}
              artifactType={outlineArtifactType}
              title="Generated Outline"
              onArtifactSelect={(artifactId) => setSelectedOutlineId(artifactId)}
            />

            <Box className={classes.nextStepSection}>
              <Typography className={classes.nextStepSectionTitle}>
                Configure Next Step — Slide Deck Generation
              </Typography>

              <InstructionsComponent
                ref={instructionsComponentRef}
                projectId={projectId}
                stage={2}
                onInstructionsChange={handleInstructionsChangeForStage(2)}
                onTemplateChange={handleTemplateChange}
                autoCreateIfMissing
              />

              <Divider className={classes.sectionDivider} />

              <FileUploaderComponent
                projectId={projectId}
                stage={2}
                selectedFileIds={selectedFileIdsByStage[2]}
                onSelectionChange={handleFileSelectionChangeForStage(2)}
              />

              <Box className={classes.nextButtonContainer}>
                <Button
                  color="quCreateColor"
                  onClick={() => handleGenerateWithAutoSave(PIPELINE_KEYS.outlineNext)}
                >
                  Generate Slide Deck →
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 3:
        // Slide Deck Stage
        return (
          <Box className={classes.stageContainer}>
            <TexPdfViewer
              projectId={projectId}
              stage={3}
              artifactType="slide_deck"
              onArtifactSelect={(artifactId) => setSelectedSlideDeckPdfId(artifactId)}
            />

            <Box className={classes.nextStepSection}>
              <Typography className={classes.nextStepSectionTitle}>
                Configure Next Step — Lecture Notes Generation
              </Typography>

              <InstructionsComponent
                ref={instructionsComponentRef}
                projectId={projectId}
                stage={3}
                onInstructionsChange={handleInstructionsChangeForStage(3)}
                onTemplateChange={handleTemplateChange}
                autoCreateIfMissing
              />

              <Divider className={classes.sectionDivider} />

              <FileUploaderComponent
                projectId={projectId}
                stage={3}
                selectedFileIds={selectedFileIdsByStage[3]}
                onSelectionChange={handleFileSelectionChangeForStage(3)}
              />

              <Box className={classes.nextButtonContainer}>
                <Button
                  color="quCreateColor"
                  onClick={() => handleGenerateWithAutoSave(PIPELINE_KEYS.slideDeckNext)}
                >
                  Generate Lecture Notes →
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 4:
        // Lecture Notes Stage
        return (
          <Box className={classes.stageContainer}>
            <TexPdfViewer
              projectId={projectId}
              stage={4}
              artifactType="lecture_notes"
              onArtifactSelect={(artifactId) => setSelectedLectureNotesPdfId(artifactId)}
            />

            <Box className={classes.nextStepSection}>
              <Typography className={classes.nextStepSectionTitle}>
                Configure Next Step — Slide Deck with Notes Generation
              </Typography>

              <InstructionsComponent
                ref={instructionsComponentRef}
                projectId={projectId}
                stage={4}
                onInstructionsChange={handleInstructionsChangeForStage(4)}
                onTemplateChange={handleTemplateChange}
                autoCreateIfMissing
              />

              <Divider className={classes.sectionDivider} />

              <FileUploaderComponent
                projectId={projectId}
                stage={4}
                selectedFileIds={selectedFileIdsByStage[4]}
                onSelectionChange={handleFileSelectionChangeForStage(4)}
                autoIncludeStages={[2, 3]}
              />

              <Box className={classes.nextButtonContainer}>
                <Button
                  color="quCreateColor"
                  onClick={() => handleGenerateWithAutoSave(PIPELINE_KEYS.lectureNotesNext)}
                >
                  Generate Slide Deck with Notes →
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 5:
        // Slide Deck with Notes Stage (Final)
        return (
          <Box className={classes.stageContainer}>
            <TexPdfViewer
              projectId={projectId}
              stage={5}
              artifactType="slide_deck_with_notes"
            />

            <Divider className={classes.sectionDivider} />

            <FileUploaderComponent
              projectId={projectId}
              stage={5}
              selectedFileIds={selectedFileIdsByStage[5]}
              onSelectionChange={handleFileSelectionChangeForStage(5)}
              showUploader={false}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  // Initial page load
  if (loading.isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress className={classes.loaderClass} />
        <Typography
          variant="h5"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Divider style={{ marginBottom: 16 }} />

      <HeadingWithDescription
        title={project.name}
        shortDescription={project.description}
        identifier="project"
        dialogTitle="Edit Project Details"
        onChange={handleProjectDetailsChange}
      />

      {/* Stepper AppBar */}
      <AppBar
        position="static"
        className={classes.header}
        style={{
          border: "2px solid #20677b",
          borderRadius: "6px",
          margin: 0,
          marginTop: -16,
          padding: 0,
          minHeight: "40px",
        }}
      >
        <Toolbar style={{ minHeight: "40px", padding: "4px 8px" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            <Stepper
              activeStep={activeStep}
              connector={<QontoConnector />}
              size="small"
              className={classes.stepperRoot}
              style={{
                flexGrow: 1,
                margin: 0,
                padding: "4px 0",
              }}
            >
              {stepsWithIntermediate.map((label, index) => {
                const isIntermediateIndex =
                  stepperInBetween && index === baseStepIndex + 1;
                const isDisabled = stepperInBetween
                  ? index > baseStepIndex + 1
                  : index > completedTill;

                const realIndex =
                  stepperInBetween && index > baseStepIndex ? index - 1 : index;

                return (
                  <Step
                    key={`${label}-${index}`}
                    onClick={() => {
                      if (isIntermediateIndex) {
                        setSuppressContentOnly(false);
                      } else if (!isDisabled) {
                        handleStepChange(realIndex);
                      }
                    }}
                    style={{ padding: "0 4px", cursor: isDisabled ? "default" : "pointer" }}
                  >
                    <StepLabel
                      StepIconComponent={
                        isIntermediateIndex ? LoadingStepIcon : undefined
                      }
                      StepIconProps={
                        !isDisabled && !isIntermediateIndex
                          ? {
                              classes: {
                                root: classes.stepIcon,
                                active: classes.activeStepIcon,
                                completed: classes.completedStepIcon,
                              },
                            }
                          : {}
                      }
                      sx={{
                        "& .MuiStepLabel-label": {
                          display: "none",
                        },
                        "&:hover .MuiStepLabel-label": {
                          display: "block",
                          position: "absolute",
                          backgroundColor: "#20677b",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                          marginTop: "8px",
                          zIndex: 1000,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        },
                        "& .MuiStepLabel-iconContainer": {
                          paddingRight: 0,
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Stage Header */}
      {!contentInBetween && <StageHeader activeTab={activeTab} />}

      {/* Main Content */}
      <Box>{renderStageContent()}</Box>
    </Box>
  );
}
